import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowLeft, MonitorSmartphone, FileDown, MessagesSquare } from 'lucide-react';
import { Navbar, Footer } from '@/components/Layout';
import { ClaimIdPanel } from '@/components/spec/ClaimIdPanel';
import { SpokenCodePanel } from '@/components/spec/SpokenCodePanel';
import { CaseTimelinePanel } from '@/components/spec/CaseTimelinePanel';

const VECTORS_URL = '/downloads/kojiru-claim-records-test-vectors-draft-0.2.json';
const TIMELINE_VECTORS_URL = '/downloads/kojiru-case-timeline-test-vectors-draft-0.3.json';
const PROPOSAL_URL = '/downloads/kojiru-claim-records-draft-0.4-proposal.pdf';

export default function ClaimRecordChecker() {
  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="claim-checker-page">
      <Helmet><title>Claim Record Checker | Draft 0.4 Proposal | Kojiru</title></Helmet>
      <Navbar />
      <main className="pt-28 pb-20 px-6">
        <div className="container mx-auto max-w-5xl">
          <Link to="/specs/claim-records" className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-white transition-colors mb-8" data-testid="claim-checker-back-link"><ArrowLeft className="w-3.5 h-3.5" /> Claim Records</Link>
          <div className="text-xs uppercase tracking-widest text-amber-300 mb-3">Draft 0.4 proposal · For comment</div>
          <h1 className="text-4xl sm:text-5xl font-bold leading-tight mb-4" data-testid="claim-checker-title">Claim record checker</h1>
          <p className="text-base text-gray-400 max-w-3xl leading-relaxed mb-6">Compute a claim ID and its 7-character spoken code, check a code heard on a call, or check that a case timeline is complete and in order.</p>
          <div className="rounded-2xl border border-sky-500/30 bg-sky-500/[0.05] p-5 flex gap-3 mb-6 max-w-3xl" data-testid="claim-checker-local-notice">
            <MonitorSmartphone className="w-5 h-5 text-sky-300 shrink-0 mt-0.5" />
            <p className="text-sm text-sky-100/80 leading-relaxed">Everything runs on your device. Nothing you type is sent to Kojiru or stored. A matching code shows only that the numbers line up. It is not proof of authority, says nothing about any claim, and is not a determination by Kojiru. Provided as is, without warranty.</p>
          </div>
          <div className="flex flex-wrap gap-4 mb-8 text-xs">
            <a href={VECTORS_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-amber-300 hover:text-amber-200" data-testid="claim-vectors-download"><FileDown className="w-3.5 h-3.5" /> Test vectors (JSON)</a>
            <a href={TIMELINE_VECTORS_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-amber-300 hover:text-amber-200" data-testid="timeline-vectors-download"><FileDown className="w-3.5 h-3.5" /> Timeline test vectors (JSON)</a>
            <a href={PROPOSAL_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-amber-300 hover:text-amber-200" data-testid="claim-proposal-download"><FileDown className="w-3.5 h-3.5" /> Draft 0.4 proposal (PDF)</a>
            <Link to="/specs/claim-records/comments" className="inline-flex items-center gap-1.5 text-amber-300 hover:text-amber-200" data-testid="claim-comments-link"><MessagesSquare className="w-3.5 h-3.5" /> Comments and how they were handled</Link>
          </div>
          <div className="space-y-6">
            <ClaimIdPanel />
            <SpokenCodePanel />
            <CaseTimelinePanel />
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
